// Videos cortos (videos masivos de producto) — ADR-2026-10-22-videos-masivos-diseno-de-produccion,
// ADR-2026-10-30-videos-cortos-con-el-agente.
//
// Todo lo que toca la red vive acá: la pantalla queda con estado y dibujo. El backend encola un video por producto
// (`/v1/videos/masivo`), con el id del lote como `execution_id` de todas las filas; acá se piden los guiones antes
// del lote, se lanza, se consulta el estado de cada video y se cancela lo que todavía no arrancó.

import { atlasBackendFetch, atlasBackendGet } from '../../../../lib/atlasBackend';
import { fetchClonedVoices } from '../../../../lib/clonedVoicesApi';
import { firmarGenerada } from '../../../../lib/videoGenerado';
import { supabase } from '../../../../lib/supabase';

/** Precio fijo por video (espejo de VIDEO_MASIVO_CREDITOS del backend). */
export const CREDITOS_POR_VIDEO = 12;
/** Rehacer sólo la voz de un video ya salido (espejo de VIDEO_MASIVO_VOZ_CREDITOS; ADR-2026-10-31-videos-cortos-rehacer-solo-la-voz). */
export const CREDITOS_POR_VOZ = 2;
/** Productos por tanda (espejo de VIDEO_MASIVO_MAX_PRODUCTOS). */
export const MAX_PRODUCTOS = 50;
export const MAX_INDICACION = 500;

/**
 * `auto`: Atlas elige por producto según para quién es (hombre, mujer, o alternando si es para todos). Si no, el id de
 * una voz (del catálogo entero del Estudio o una clonada de la empresa: ADR-2026-10-30-videos-cortos-voces-del-estudio);
 * `mujer`/`hombre` siguen valiendo (Valeria / Facundo).
 */
export type VozDeVideo = 'auto' | 'mujer' | 'hombre' | (string & {});

/** Una voz argentina del modo, con la URL de su muestra (grabada con el mismo modelo que los videos). */
export interface VozDelModo {
  id: string;
  nombre: string;
  sexo: 'mujer' | 'hombre';
  descripcion: string | null;
  muestra_url: string | null;
}

/**
 * Una voz como la ofrece el generador de voz en off del Estudio: las del catálogo `voces` (con idioma y acento) y
 * las clonadas de la empresa (ADR-2026-10-30-videos-cortos-voces-del-estudio).
 */
export interface VozDelEstudio {
  id: string;
  nombre: string;
  /** Como está en la tabla: "Femenina" / "Masculina" (las clonadas no lo tienen). */
  sexo: string | null;
  idioma: string | null;
  acento: string | null;
  descripcion: string | null;
  /** La muestra para escucharla: la del modo (v4) si es argentina, si no la del catálogo. */
  muestraUrl: string | null;
  clonada: boolean;
}

export interface OpcionesDeLote {
  /** `null` = sin voz en off (y sin subtítulos). La voz de la tanda. */
  voz: VozDeVideo | null;
  subtitulos: boolean;
  indicacion: string;
}

/** El guion tal como viaja con el backend (`GuionDelFront`). */
export interface GuionParaElLote {
  rubro?: string | null;
  idea?: string | null;
  escenas: string[];
  prompt: string;
  locucion: { texto: string; partes: Array<{ toma: number; texto: string }> } | null;
  publico?: 'hombres' | 'mujeres' | 'todos' | null;
  fotos_usadas: number[];
  /** Los nombres de las fotos que vio el director (ADR-2026-10-31-videos-cortos-las-fotos-del-guion-van-por-nombre). */
  fotos_nombres?: string[];
}

export interface VideoParaElLote {
  variante_id: string;
  /** Sin el campo, la voz de la tanda. */
  voz?: VozDeVideo | null;
  guion: GuionParaElLote;
  /** Rehacer sólo la voz de este video ya entregado (su fila de `imagenes_generadas`). */
  solo_voz_de?: string;
}

export interface EstadoDeVideo {
  job_id: string;
  variante_id: string | null;
  estado: 'en_cola' | 'generando' | 'listo' | 'fallo' | 'cancelado';
  etapa: string | null;
  error: string | null;
  creditos: number;
}

export interface EstadoDeLote {
  lote_id: string;
  total: number;
  listos: number;
  fallidos: number;
  cancelados: number;
  en_curso: number;
  terminado: boolean;
  videos: EstadoDeVideo[];
}

export interface VideoListo {
  id: string;
  varianteId: string | null;
  /** La ruta en el bucket `generadas`: la URL firmada vence a la hora y se vuelve a firmar con esto. */
  ruta: string;
  url: string;
  poster?: string;
  locucion: string | null;
  /** El backend guardó el video sin voz: se le puede rehacer sólo la voz. */
  conFuente?: boolean;
}

/** Lo que cuesta una tanda. Puro. */
export const costoDeLote = (productos: number) => productos * CREDITOS_POR_VIDEO;

/** Qué está haciendo un video, en palabras de la persona. Puro. */
export function textoDeEtapa(v: Pick<EstadoDeVideo, 'estado' | 'etapa'>): string {
  if (v.estado === 'en_cola') return 'En la fila';
  if (v.estado === 'listo') return 'Listo';
  if (v.estado === 'fallo') return 'No salió';
  if (v.estado === 'cancelado') return 'Cancelado';
  switch (v.etapa) {
    case 'fotos':
      return 'Escribiendo el guion';
    case 'guion':
      return 'Grabando la voz';
    case 'voz':
      return 'Filmando el video';
    case 'motor':
      return 'Mejorando la calidad';
    case 'escalar':
      return 'Armando el video final';
    case 'armar':
    case 'upload_generadas':
    case 'deliver_and_charge':
      return 'Entregando';
    default:
      return 'Preparando';
  }
}

/** El mensaje cuando el backend no dio uno que se pueda mostrar. */
export const MENSAJE_GENERICO = 'No se pudo hacer el pedido. Probá de nuevo en un rato.';

/**
 * Lo que se le muestra a la persona de un error del backend. Los mensajes de validación de zod
 * ("Required", "Expected string…") son para el programador: llegan cuando el front y el backend no
 * coinciden (p. ej. el backend todavía sin desplegar) y se cambian por el genérico. Puro.
 */
export function mensajeLegible(texto: string | null | undefined): string {
  const t = (texto ?? '').trim();
  if (!t || t === 'no_disponible') return MENSAJE_GENERICO;
  const tecnico = /^(Required|Expected\b|Invalid\b|String must|Array must|Number must|Unrecognized key)/;
  return t.split('; ').some((parte) => tecnico.test(parte.replace(/^[\w.[\]]+: /, ''))) ? MENSAJE_GENERICO : t;
}

/** Un error del backend de Videos cortos, con lo que la pantalla necesita para reaccionar. */
export type ErrorDeVideosCortos = Error & {
  status?: number;
  tipo?: string;
  /** Con un 429 de "ya hay una tanda en curso": cuál es, para volver a seguirla. */
  loteEnCurso?: string;
};

async function errorDe(res: Response): Promise<ErrorDeVideosCortos> {
  const j = (await res.json().catch(() => ({}))) as { error?: string; error_type?: string; lote_en_curso?: string };
  const e = new Error(mensajeLegible(j.error)) as ErrorDeVideosCortos;
  e.status = res.status;
  e.tipo = j.error_type;
  if (typeof j.lote_en_curso === 'string' && j.lote_en_curso) e.loteEnCurso = j.lote_en_curso;
  return e;
}

/** El cuerpo del lote: cada video con su guion y, si tiene una propia, su voz. Puro. */
export function cuerpoDelLote(loteId: string, videos: VideoParaElLote[], o: OpcionesDeLote): Record<string, unknown> {
  return {
    execution_id: loteId,
    videos: videos.map((v) => ({ variante_id: v.variante_id, ...(v.voz !== undefined ? { voz: v.voz } : {}), guion: v.guion, ...(v.solo_voz_de ? { solo_voz_de: v.solo_voz_de } : {}) })),
    voz: o.voz,
    subtitulos: o.voz ? o.subtitulos : false,
    ...(o.indicacion.trim() ? { indicacion: o.indicacion.trim().slice(0, MAX_INDICACION) } : {}),
  };
}

/**
 * Lanza una tanda. El id del lote lo genera el front: un reintento con el mismo id no duplica nada (el backend devuelve
 * lo que ya existe y encola sólo lo que falta). `noEncolados`: los productos que no entraron (se reintentan igual).
 */
export async function lanzarLote(loteId: string, videos: VideoParaElLote[], o: OpcionesDeLote): Promise<{ creditosTotal: number; noEncolados: string[] }> {
  const res = await atlasBackendFetch('/v1/videos/masivo', cuerpoDelLote(loteId, videos, o));
  if (res.status !== 202) throw await errorDe(res);
  const j = (await res.json()) as { creditos_total: number; no_encolados?: string[] };
  return { creditosTotal: j.creditos_total, noEncolados: j.no_encolados ?? [] };
}

export interface PedidoDeGuiones {
  productos: Array<{ variante_id: string; indicacion?: string }>;
  voz: VozDeVideo | null;
  indicacion?: string;
}

/** Pide los guiones (gratis): un job por producto. Devuelve los `execution_id` para polear. */
export async function pedirGuiones(p: PedidoDeGuiones): Promise<{ guiones: Array<{ variante_id: string; execution_id: string }>; noEncolados: string[]; error: string | null }> {
  const res = await atlasBackendFetch('/v1/videos/masivo/guiones', p as unknown as Record<string, unknown>);
  const j = (await res.json().catch(() => ({}))) as { guiones?: Array<{ variante_id: string; execution_id: string }>; error?: string };
  const guiones = Array.isArray(j.guiones) ? j.guiones : [];
  const enCola = new Set(guiones.map((g) => g.variante_id));
  const noEncolados = p.productos.map((x) => x.variante_id).filter((id) => !enCola.has(id));
  if (!res.ok && res.status !== 429) {
    return { guiones, noEncolados, error: mensajeLegible(j.error) };
  }
  return { guiones, noEncolados, error: null };
}

export interface EjecucionDeGuion {
  execution_id: string;
  status: string;
  output: { variante_id?: string; guion?: GuionParaElLote } | null;
  error: string | null;
}

export async function verGuiones(ids: string[]): Promise<EjecucionDeGuion[]> {
  const res = await atlasBackendGet(`/v1/videos/masivo/guiones?ids=${ids.join(',')}`);
  if (!res.ok) throw await errorDe(res);
  const j = (await res.json()) as { ejecuciones?: EjecucionDeGuion[] };
  return Array.isArray(j.ejecuciones) ? j.ejecuciones : [];
}

/** Las voces argentinas del catálogo. La primera vez el backend graba las muestras que falten: puede tardar unos segundos. */
export async function vocesDelModo(): Promise<VozDelModo[]> {
  const res = await atlasBackendGet('/v1/videos/masivo/voces');
  if (!res.ok) throw await errorDe(res);
  return ((await res.json()) as { voces: VozDelModo[] }).voces;
}

/**
 * Las mismas voces que ofrece el Estudio: el catálogo `voces` entero (ordenado por nombre, como allá) y las clonadas de
 * la empresa. Las argentinas llevan la muestra v4 del modo si el backend la tiene; si ese pedido falla, salen con la
 * muestra del catálogo.
 */
export async function vocesDelEstudio(companyId: string | null): Promise<VozDelEstudio[]> {
  const [catalogo, clonadas, delModo] = await Promise.all([
    supabase.from('voces').select('id, nombre, sexo, descripcion, preview_url, idioma, acento').order('nombre'),
    companyId ? fetchClonedVoices(companyId).catch(() => []) : Promise.resolve([]),
    vocesDelModo().catch(() => [] as VozDelModo[]),
  ]);
  const muestraDelModo = new Map(delModo.map((v) => [v.id, v.muestra_url]));
  const filas = (catalogo.data ?? []) as Array<{ id: string; nombre: string; sexo: string | null; descripcion: string | null; preview_url: string | null; idioma: string | null; acento: string | null }>;
  return [
    ...clonadas.map((c) => ({ id: c.elevenlabsVoiceId, nombre: c.nombre, sexo: null, idioma: null, acento: null, descripcion: 'Tu voz clonada', muestraUrl: null, clonada: true })),
    ...filas.map((f) => ({
      id: f.id,
      nombre: f.nombre,
      sexo: f.sexo,
      idioma: f.idioma,
      acento: f.acento,
      descripcion: f.descripcion,
      muestraUrl: muestraDelModo.get(f.id) ?? f.preview_url ?? null,
      clonada: false,
    })),
  ];
}

export async function verLote(loteId: string): Promise<EstadoDeLote | null> {
  const res = await atlasBackendGet(`/v1/videos/masivo/${loteId}`);
  if (res.status === 404) return null;
  if (!res.ok) throw await errorDe(res);
  return (await res.json()) as EstadoDeLote;
}

/** Los videos ya entregados del lote (todas sus filas comparten el `execution_id` del lote), firmados. */
export async function videosListos(loteId: string, companyId: string): Promise<VideoListo[]> {
  const { data } = await supabase
    .from('imagenes_generadas')
    .select('id, file_name, variante_id, datos_generacion')
    .eq('id_company', companyId)
    .eq('execution_id', loteId)
    .eq('has_error', false);
  const filas = (data ?? []) as Array<{ id: string; file_name: string; variante_id: string | null; datos_generacion: Record<string, unknown> | null }>;
  const firmados = await Promise.all(
    filas.map(async (f): Promise<VideoListo | null> => {
      const ruta = `${companyId}/full/${f.file_name}`;
      const [url, poster] = await Promise.all([
        firmarGenerada(ruta),
        firmarGenerada(`${companyId}/opt/${f.file_name.replace(/\.mp4$/i, '.png')}`),
      ]);
      if (!url) return null;
      const extra = (f.datos_generacion?.video_masivo ?? null) as { locucion?: string | null; fuente?: string | null } | null;
      return { id: f.id, varianteId: f.variante_id, ruta, url, ...(poster ? { poster } : {}), locucion: extra?.locucion ?? null, ...(extra?.fuente ? { conFuente: true } : {}) };
    }),
  );
  return firmados.filter((v): v is VideoListo => !!v);
}
